import { useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@core/auth/AuthProvider';
import { redirectIfProfileIncomplete } from '@core/auth';
import { getAuthToken } from '@core/api';
import { loadStoredBookingState } from '@features/booking/state/BookingProvider';
import { Skeleton } from '@shared/components/ui/skeleton';
import { usePasswordlessAuth } from '../hooks/usePasswordlessAuth';

function resolvePostLoginPath(): string {
  const booking = loadStoredBookingState();
  if (!booking?.selectedTime || !booking.diners || !booking.selectedDate) {
    return '/';
  }

  const qs = new URLSearchParams();
  qs.set('ps', String(booking.diners));
  qs.set('d', booking.selectedDate);
  if (booking.service) qs.set('st', booking.service);
  qs.set('t', booking.selectedTime);
  if (booking.flowType) qs.set('ft', booking.flowType);
  if (booking.isEarlyAccessFlow) qs.set('ea', '1');
  return `/auth-return-complete?${qs.toString()}`;
}

export default function AuthMagicLinkPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { loginWithToken, authState, isLoading } = useAuth();
  const processedRef = useRef(false);

  const { verifyToken, error } = usePasswordlessAuth({
    restaurant: '',
    onSuccess: async (jwt, email, flowId) => {
      await loginWithToken(jwt, email ?? '', flowId);
      // Note: we don't gate on isProfileIncomplete here because `authState`
      // is captured at render time, before loginWithToken's setAuthState
      // updates have flushed — the closure sees stale name/phone (usually
      // undefined for first-time logins) and would trigger a false
      // redirect to /complete-profile. The ProfileCompletionEnforcer
      // catches the real case on the next render with fresh state.
      navigate(resolvePostLoginPath(), { replace: true });
    },
  });

  useEffect(() => {
    if (isLoading) return;
    if (processedRef.current) return;
    processedRef.current = true;

    if (!token) {
      navigate('/', { replace: true });
      return;
    }

    if (authState.isAuthenticated && getAuthToken()) {
      const postLoginPath = resolvePostLoginPath();
      if (redirectIfProfileIncomplete(navigate, authState, postLoginPath)) return;
      navigate(postLoginPath, { replace: true });
      return;
    }

    verifyToken(token);
  }, [isLoading, token, verifyToken, navigate, authState]);

  useEffect(() => {
    if (!error) return;

    if (getAuthToken() && authState.isAuthenticated) {
      const postLoginPath = resolvePostLoginPath();
      if (redirectIfProfileIncomplete(navigate, authState, postLoginPath)) return;
      navigate(postLoginPath, { replace: true });
      return;
    }

    toast.error(error);
    navigate('/', { replace: true });
  }, [error, authState, navigate]);

  return (
    <div className="min-h-screen bg-card flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border max-w-2xl mx-auto w-full">
        <Skeleton className="h-8 w-12" />
        <Skeleton className="h-6 w-32" />
        <Skeleton className="h-8 w-8 rounded-full" />
      </header>
      <main className="flex-1 p-6 max-w-2xl mx-auto w-full">
        <div className="text-center mb-8">
          <Skeleton className="h-10 w-48 mx-auto mb-3" />
          <Skeleton className="h-6 w-32 mx-auto" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-12 w-full rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        </div>
        <div className="mt-10">
          <Skeleton className="h-8 w-40 mb-4" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-48 rounded-xl" />
            <Skeleton className="h-48 rounded-xl" />
          </div>
        </div>
      </main>
    </div>
  );
}
